import { Injectable, OnDestroy, OnInit } from "@angular/core";
import { Observable, Subscription, Subject } from "rxjs";

// BFX
import * as CubiqTS from "shared/cubiq-services";
import { ProfileSettingImpl } from "shared/cubiq-model-multi-asset";
import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";
// import { BfxAuthService } from '../bfx-common/services/bfx-auth.service';
import { GoldenLayoutService } from "../golden-layout/golden-layout-service";
import { LoggerService } from "@bfx/logger";
import { UserAuthPromiseService } from "../../shared/cubiq-services/auth/impl/UserAuthPromiseService";
const logger = LoggerService.for(__dirname);

/**
 * 1.1 Upgraded for new Fill% col
 * 1.0 Initial
 */
const PROFILE_SETTINGS_VERSION: string = "BfxHedgeOrderMonitorComponent:1.1";
const HEDGE_ORDERS_BLOTTER: string = "BfxHedgeOrderMonitorComponent";
const STAGING_BLOTTER: string = "BfxHedgeOrderStagingGridComponent";

@Injectable()
export class BfxHedgeOrderMonitorService implements OnInit, OnDestroy {
  // protected authSubscription: Subscription;
  protected saveProfileSubscription: Subscription;

  // private columnHedgeOrdersBlotterConfiguration: any = {};
  // private columnStagingBlotterConfiguration: any = {};
  private static stagingOrderCount: number = 0;
  private profileConfigAction = new Subject<any>();

  public getProfileConfigAction$: Observable<any> = this.profileConfigAction.asObservable();
  public savedProfileConfig: any = {};
  public hasProfileConfig: boolean = false;

  constructor(
    protected profileActionService: CubiqTS.ProfilePromiseActionService,
    protected userProfileService: BfxUserProfileService,
    protected layoutService: GoldenLayoutService,
    _authService: UserAuthPromiseService,
  ) {
    this.clearSessionStorage();

    _authService.registerHOM(this);
  }

  ngOnInit() {
    // this.subscribeToAuth();
    this.getProfileConfig();
    this.subscribeToSaveProfile();
  }

  ngOnDestroy() {
    if (this.saveProfileSubscription) {
      this.saveProfileSubscription.unsubscribe();
    }
  }

  private getProfileSettingKey(): string {
    return `${CubiqTS.ProfileSettingKeys.userProfileDefaultBlotter}BfxHedgeOrderMonitorComponent`;
  }

  private clearSessionStorage(): void {
    window.addEventListener("unload", (event) => {
      event.preventDefault();
      for (let key in sessionStorage) {
        if (
          sessionStorage.hasOwnProperty(key) &&
          (key === HEDGE_ORDERS_BLOTTER || key === STAGING_BLOTTER)
        )
          sessionStorage.removeItem(key);
      }
    });
  }

  /**
   * @description Receives column configurations from Hedge Order Monitor when closing the window.
   * @param {*} config
   * @returns {Observable<any>}
   * @memberof BfxHedgeOrderMonitorService
   */
  // public setHedgeOrderBlotterColumnsConfig(config: any): Observable<any> {
  // 	return this.columnHedgeOrdersBlotterConfiguration = config;
  // }

  /**
   * @description Returns column configurations for Hedge Order Blotter when component is initialized.
   * @returns Object
   * @memberof BfxHedgeOrderMonitorService
   */
  // public getHedgeOrderBlotterColumnsConfig() {
  // 	return this.columnHedgeOrdersBlotterConfiguration;
  // }

  /**
   * @description Receives column configurations from Staging Blotter when closing the window.
   * @param {*} config
   * @returns {Observable<any>}
   * @memberof BfxHedgeOrderMonitorService
   */
  // public setStagingBlotterColumnsConfig(config: any): Observable<any> {
  // 	return this.columnStagingBlotterConfiguration = config;
  // }

  /**
   * @description Returns column configurations for Staging Blotter when component is initialized.
   * @returns object
   * @memberof BfxHedgeOrderMonitorService
   */
  // public getStagingBlotterColumnsConfig() {
  // 	return this.columnStagingBlotterConfiguration;
  // }

  public getStagingOrderCount(): number {
    return BfxHedgeOrderMonitorService.stagingOrderCount;
  }

  public updateStagingOrderCount(increment: number): void {
    BfxHedgeOrderMonitorService.stagingOrderCount += increment;
  }

  protected getConfigToSaveAsDefault(): any {
    return {
      version: PROFILE_SETTINGS_VERSION,
      columnsConfiguration: sessionStorage.getItem(HEDGE_ORDERS_BLOTTER),
      stagingBlotterConfiguration: sessionStorage.getItem(STAGING_BLOTTER),
    };
  }

  public saveConfigToSession(configName: string, configValue: object): void {
    sessionStorage.setItem(configName, JSON.stringify(configValue));
  }

  public restoreConfigFromSession(configName: string): any {
    if (configName in sessionStorage) {
      return JSON.parse(sessionStorage.getItem(configName));
    }
    return null;
  }

  // /**
  //  * @description Gets triggered when a user logs out, and overwrites profile config.
  //  * @private
  //  * @memberof BfxHedgeOrderMonitorComponent
  //  */
  // private subscribeToAuth(): void {
  //     this.authSubscription = this._authService.authStatus$.subscribe(
  // 		(status: boolean) => {
  // 			if (!status) { // user is logged out
  // 				this.saveAsDefault();
  // 			}
  // 		}
  // 	);
  // }

  /**
   * @description Gets triggered when a user saves a profile, and overwrites profile config.
   * @private
   * @memberof BfxHedgeOrderMonitorComponent
   */
  private subscribeToSaveProfile(): void {
    this.saveProfileSubscription = this.layoutService.savedProfileAction$.subscribe(
      (isSaved: boolean) => {
        if (isSaved) {
          this.saveProfileConfig();
        }
      },
    );
  }

  private hasColumnDefinitions(): boolean {
    return (
      sessionStorage.hasOwnProperty(HEDGE_ORDERS_BLOTTER) &&
      sessionStorage.hasOwnProperty(STAGING_BLOTTER)
    );
  }

  /**
   * @description Overwrites profile settings config.
   * @memberof BfxHedgeOrderMonitorService
   */
  public saveProfileConfig(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      let profileSettings: Array<ProfileSettingImpl> = [];
      let configData = JSON.stringify(this.getConfigToSaveAsDefault());

      if (this.hasColumnDefinitions()) {
        profileSettings.push(
          this.userProfileService.createProfileSettingObject(
            this.getProfileSettingKey(),
            configData,
          ),
        );
        resolve(configData);

        this.profileActionService.updateProfile(profileSettings, false).then(
          () => {},
          (reason) => {
            logger.out.warn(`${this.constructor.name}.saveAsDefault() failed ${reason}`);
            reject(reason);
          },
        );
      }
    });
  }

  /**
   * @description Gets previously saved column configurations
   * @private
   * @memberof BfxHedgeOrderMonitorService
   */
  public getProfileConfig(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.profileActionService.getProfileConfig(this.getProfileSettingKey()).then(
        (value: any) => {
          let config: any = null;

          if (value != "" && value != null) {
            config = JSON.parse(value);
          }

          if (config && config.version === PROFILE_SETTINGS_VERSION) {
            this.hasProfileConfig = true;
            this.savedProfileConfig = config;
            resolve(config);
          } else {
            resolve(null);
          }
        },
        (reason) => {
          logger.out.warn(`${this.constructor.name}: failed to load profile config`);
          reject(reason);
        },
      );
    });
  }
}
